import React, { useEffect, useMemo, useState } from "react";
import {
    Copy,
    Check,
    Activity,
    HeartPulse,
    Pill,
    Smile,
    CalendarClock,
    Eye,
    Link2,
    Printer,
    Mail,
    MessageSquareReply,
    CheckCircle2,
} from "lucide-react";
import { useClara } from "../contexts/ClaraContext";
import { useData } from "../contexts/DataContext";
import HistoryChart from "../components/HistoryChart";
import VoiceReply from "../components/VoiceReply";
import { APPOINTMENT, USER } from "../data/sampleData";

const SHARE_URL = "https://clearpath.app/care/maria-weekly-summary";

function weekRangeLabel() {
    const now = new Date();
    const start = new Date(now);
    start.setDate(start.getDate() - 6);
    const fmt = (d) =>
        d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return `${fmt(start)} – ${fmt(now)}`;
}

export const CaregiverShare = () => {
    const { speak } = useClara();
    const {
        glucoseHistory,
        bpHistory,
        adherence,
        moodLog,
        primaryContact,
        alertContact,
        addReply,
        repliesByMessage,
    } = useData();
    const [copied, setCopied] = useState(false);
    const [commenting, setCommenting] = useState(false);
    const comments = repliesByMessage["caregiver-comment"] || [];

    useEffect(() => {
        speak(
            `Weekly summary for ${USER.name}. Share this read-only view with ${primaryContact?.name || "your contacts"} and ${alertContact?.name || "your family"}.`,
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const glucoseStats = useMemo(() => {
        const vals = glucoseHistory.map((g) => g.value);
        const avg = vals.length
            ? Math.round(vals.reduce((s, v) => s + v, 0) / vals.length)
            : 0;
        const inRange = vals.filter((v) => v >= 80 && v <= 140).length;
        const pct = vals.length ? Math.round((inRange / vals.length) * 100) : 0;
        return { avg, pct, count: vals.length };
    }, [glucoseHistory]);

    const bpStats = useMemo(() => {
        if (!bpHistory.length) return { sys: 0, dia: 0, count: 0 };
        const sys = Math.round(
            bpHistory.reduce((s, b) => s + b.systolic, 0) / bpHistory.length,
        );
        const dia = Math.round(
            bpHistory.reduce((s, b) => s + b.diastolic, 0) / bpHistory.length,
        );
        return { sys, dia, count: bpHistory.length };
    }, [bpHistory]);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(SHARE_URL);
        } catch (err) {
            console.warn("clipboard.writeText failed (demo fallback):", err);
        }
        setCopied(true);
        speak(
            `Share link copied. ${primaryContact?.name || "Your contact"} can open this read-only summary anytime.`,
        );
        setTimeout(() => setCopied(false), 3500);
    };

    const handlePrint = () => {
        speak(
            "Opening the print dialog. You can save your weekly summary as a PDF from there.",
        );
        // Small delay so the toast renders before print blocks the main thread
        setTimeout(() => {
            if (typeof window !== "undefined") window.print();
        }, 350);
    };

    const handleEmail = () => {
        const subject = encodeURIComponent(
            `${USER.name}'s weekly health summary`,
        );
        const body = encodeURIComponent(
            [
                `Hi,`,
                ``,
                `Here is ${USER.name}'s weekly health summary (${weekRangeLabel()}):`,
                ``,
                `• Glucose average: ${glucoseStats.avg} mg/dL (${glucoseStats.pct}% in range, ${glucoseStats.count} readings)`,
                `• Blood pressure average: ${bpStats.sys}/${bpStats.dia} mmHg (${bpStats.count} readings)`,
                `• Medication adherence: ${Math.round(adherence * 100)}%`,
                `• Next appointment: ${APPOINTMENT.doctor} — ${APPOINTMENT.when}`,
                ``,
                `Read-only dashboard:`,
                SHARE_URL,
                ``,
                `Sent with care from MyClearPath.`,
            ].join("\n"),
        );
        if (typeof window !== "undefined") {
            window.location.href = `mailto:?subject=${subject}&body=${body}`;
        }
        speak(
            `Opening your email app with this week's summary ready to send to ${primaryContact?.name || "your contact"}.`,
        );
    };

    return (
        <div className="space-y-5" data-testid="caregiver-share-screen">
            {/* Header card */}
            <section
                className="rounded-3xl p-6 text-white shadow-lg relative overflow-hidden"
                style={{
                    background:
                        "linear-gradient(135deg, #0A3D7A 0%, #1B5BA8 55%, #2E7AB8 100%)",
                }}
            >
                <span
                    aria-label="Read only"
                    data-testid="readonly-badge"
                    className="inline-flex items-center gap-1.5 rounded-full bg-white/20 text-[#FFE7A8] text-[11px] font-bold uppercase tracking-wider px-3 py-1.5"
                >
                    <Eye className="w-3.5 h-3.5" aria-hidden="true" />
                    Read-only
                </span>
                <h2 className="font-display text-3xl font-bold mt-3">
                    {USER.name}'s week
                </h2>
                <p className="text-white/90 text-sm mt-1">{weekRangeLabel()}</p>
            </section>

            {/* Share link */}
            <section
                className="rounded-3xl bg-white p-5 border border-[#0A3D7A]/10 shadow-[0_4px_20px_rgba(10,61,122,0.06)]"
                data-testid="share-link-card"
            >
                <p className="text-xs uppercase tracking-wider font-semibold text-[#5A5A6E] mb-2">
                    Share with caregivers
                </p>
                <div className="flex items-center gap-2 rounded-2xl bg-[#FFF8F0] border border-[#F0EAD6] p-3">
                    <Link2 className="w-4 h-4 text-[#5A5A6E] shrink-0" />
                    <p
                        className="flex-1 min-w-0 text-sm text-[#1A1A2E] font-mono truncate"
                        data-testid="share-link-value"
                    >
                        {SHARE_URL}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={handleCopy}
                    data-testid="copy-share-link-btn"
                    aria-label="Copy share link to clipboard"
                    className={`mt-3 w-full min-h-[56px] rounded-2xl text-lg font-bold flex items-center justify-center gap-2 transition-colors shadow-sm ${
                        copied
                            ? "bg-[#0A5C36] text-white"
                            : "bg-[#0A3D7A] hover:bg-[#082F5E] text-white"
                    }`}
                >
                    {copied ? (
                        <>
                            <Check className="w-5 h-5" aria-hidden="true" />
                            Link copied
                        </>
                    ) : (
                        <>
                            <Copy className="w-5 h-5" aria-hidden="true" />
                            Copy share link
                        </>
                    )}
                </button>

                <div className="mt-3 grid grid-cols-2 gap-2 no-print">
                    <button
                        type="button"
                        onClick={handlePrint}
                        data-testid="export-pdf-btn"
                        aria-label="Export weekly summary as a PDF"
                        className="min-h-[52px] rounded-2xl bg-white border-2 border-[#0A3D7A] text-[#0A3D7A] text-base font-bold flex items-center justify-center gap-2 hover:bg-[#0A3D7A]/5 transition-colors"
                    >
                        <Printer className="w-5 h-5" aria-hidden="true" />
                        Export PDF
                    </button>
                    <button
                        type="button"
                        onClick={handleEmail}
                        data-testid="email-summary-btn"
                        aria-label="Email weekly summary"
                        className="min-h-[52px] rounded-2xl bg-white border-2 border-[#0A3D7A] text-[#0A3D7A] text-base font-bold flex items-center justify-center gap-2 hover:bg-[#0A3D7A]/5 transition-colors"
                    >
                        <Mail className="w-5 h-5" aria-hidden="true" />
                        Email
                    </button>
                </div>

                <p className="mt-3 text-xs text-[#5A5A6E] leading-snug">
                    Recipients see vitals trends and appointments only — never
                    messages, payment details, or your trusted circle.
                </p>
            </section>

            {/* Vitals stats */}
            <section
                className="rounded-3xl bg-white p-5 border border-[#0A3D7A]/10 shadow-[0_4px_20px_rgba(10,61,122,0.06)]"
                data-testid="weekly-vitals-card"
            >
                <h3 className="font-display font-bold text-lg text-[#1A1A2E] mb-4">
                    Vitals this week
                </h3>
                <div className="grid grid-cols-2 gap-3">
                    <StatTile
                        Icon={Activity}
                        label="Glucose avg"
                        value={`${glucoseStats.avg}`}
                        unit="mg/dL"
                        chip={`${glucoseStats.pct}% in range`}
                        chipColor="bg-[#E5F1EB] text-[#0A5C36]"
                        testId="stat-glucose"
                    />
                    <StatTile
                        Icon={HeartPulse}
                        label="BP avg"
                        value={`${bpStats.sys}/${bpStats.dia}`}
                        unit="mmHg"
                        chip={`${bpStats.count} readings`}
                        chipColor="bg-[#E8EEF7] text-[#0A3D7A]"
                        testId="stat-bp"
                    />
                    <StatTile
                        Icon={Pill}
                        label="Medications"
                        value={`${Math.round(adherence * 100)}%`}
                        unit="adherence"
                        chip={
                            adherence >= 0.85
                                ? "On track"
                                : "Needs attention"
                        }
                        chipColor={
                            adherence >= 0.85
                                ? "bg-[#E5F1EB] text-[#0A5C36]"
                                : "bg-[#FFE6E8] text-[#A31621]"
                        }
                        testId="stat-meds"
                    />
                    <StatTile
                        Icon={Smile}
                        label="Mood"
                        value={moodLog[0] || "—"}
                        unit="this week"
                        chip={`${moodLog.length} logs`}
                        chipColor="bg-[#F0EAD6] text-[#7A5A0A]"
                        testId="stat-mood"
                    />
                </div>
                <div className="mt-5">
                    <p className="text-xs uppercase tracking-wider font-semibold text-[#5A5A6E] mb-2">
                        Glucose · 7-day trend
                    </p>
                    <HistoryChart
                        history={glucoseHistory}
                        metric="glucose"
                        height={140}
                        testId="caregiver-glucose-chart"
                    />
                </div>
                <div className="mt-5">
                    <p className="text-xs uppercase tracking-wider font-semibold text-[#5A5A6E] mb-2">
                        Blood pressure · 7-day trend
                    </p>
                    <HistoryChart
                        history={bpHistory}
                        metric="bp"
                        height={140}
                        testId="caregiver-bp-chart"
                    />
                </div>
            </section>

            {/* Upcoming */}
            <section
                className="rounded-3xl bg-white p-5 border border-[#0A3D7A]/10 shadow-[0_4px_20px_rgba(10,61,122,0.06)]"
                data-testid="weekly-appointments-card"
            >
                <h3 className="font-display font-bold text-lg text-[#1A1A2E] mb-3">
                    Coming up
                </h3>
                <div className="flex items-center gap-3 p-3 rounded-2xl bg-[#FFF8F0] border border-[#F0EAD6]">
                    <span
                        aria-hidden="true"
                        className="shrink-0 w-10 h-10 rounded-xl bg-[#0A3D7A] text-white flex items-center justify-center"
                    >
                        <CalendarClock className="w-5 h-5" />
                    </span>
                    <div className="flex-1 min-w-0">
                        <p className="font-semibold text-[#1A1A2E] truncate">
                            {APPOINTMENT.doctor}
                        </p>
                        <p className="text-sm text-[#5A5A6E]">
                            {APPOINTMENT.specialty} · {APPOINTMENT.when}
                        </p>
                    </div>
                </div>
            </section>

            {/* Comments for caregivers */}
            <section
                className="rounded-3xl bg-white p-5 border border-[#0A3D7A]/10 shadow-[0_4px_20px_rgba(10,61,122,0.06)] no-print"
                data-testid="caregiver-comments-card"
            >
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-display font-bold text-lg text-[#1A1A2E]">
                        Leave a note
                    </h3>
                    <span className="text-xs text-[#5A5A6E]">
                        For {primaryContact?.name || "your caregiver"}
                    </span>
                </div>
                <button
                    type="button"
                    onClick={() => setCommenting(true)}
                    data-testid="leave-caregiver-comment-btn"
                    aria-label={`Leave a voice comment for ${primaryContact?.name || "your caregiver"}`}
                    className="w-full min-h-[56px] rounded-2xl bg-[#0A3D7A] hover:bg-[#082F5E] text-white text-base font-bold flex items-center justify-center gap-2 transition-colors shadow-sm"
                >
                    <MessageSquareReply className="w-5 h-5" aria-hidden="true" />
                    Leave a voice comment
                </button>

                {comments.length > 0 && (
                    <ul
                        className="mt-4 space-y-2"
                        data-testid="caregiver-comments-list"
                    >
                        {comments.map((c) => (
                            <li
                                key={c.id}
                                data-testid={`caregiver-comment-${c.id}`}
                                className="rounded-xl bg-[#E5F1EB] border border-[#0A5C36]/20 p-3 text-sm text-[#1A1A2E] leading-snug whitespace-pre-wrap flex items-start gap-2"
                            >
                                <CheckCircle2
                                    className="w-4 h-4 text-[#0A5C36] mt-0.5 shrink-0"
                                    aria-hidden="true"
                                />
                                <span>{c.text}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {commenting && (
                <VoiceReply
                    recipient={primaryContact?.name || "your caregiver"}
                    threadId="caregiver-comment"
                    onSent={(p) => addReply("caregiver-comment", p.text)}
                    onClose={() => setCommenting(false)}
                />
            )}
        </div>
    );
};

const StatTile = ({ Icon, label, value, unit, chip, chipColor, testId }) => (
    <div
        className="rounded-2xl bg-[#FFF8F0] border border-[#F0EAD6] p-3"
        data-testid={testId}
    >
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-[#5A5A6E]">
            <Icon className="w-3.5 h-3.5" aria-hidden="true" />
            {label}
        </div>
        <p className="font-display font-bold text-2xl text-[#1A1A2E] mt-0.5 leading-none">
            {value}
        </p>
        <p className="text-[11px] text-[#5A5A6E] mt-0.5">{unit}</p>
        <span
            className={`mt-2 inline-block rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${chipColor}`}
        >
            {chip}
        </span>
    </div>
);

export default CaregiverShare;
